import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Tabs from '@/shared/components/ui/Tabs';
import AppDialog from '@/shared/components/ui/AppDialog';
import { useAiText } from '@/features/system/ai/hooks/useAiText';
import type { Worker } from '../../api/workersApi';
import { fetchWorkersV2Api } from '../../api/workersApi';
import { createWorkflowApi, fetchWorkflowsApi, updateWorkflowApi, WorkflowCatalogEntry } from '../../api/workflowsApi';
import { publishWorkflowV2Api, type StartWorkflowResponse as StartWorkflowResponseV2 } from '../../api/workflowsV2Api';
import type { Handler } from '../../api/handlersApi';
import { fetchHandlersApi } from '../../api/handlersApi';
import { fetchHandlersV2Api, type HandlerSpecRow } from '../../api/handlersV2Api';
import { fetchDeviceConfigKeysApi } from '../../api/devicesApi';
import { resolveHandlerRef } from '../../utils/handlerRefs';
import type { Edge, Settings, StepDefinition, StepNode } from './types';
import { computeTreeLayoutVertical, nextStepId, safeParseJson, formatJson, toDag, fromDag } from './layout';
import WorkflowGraphView from './WorkflowGraphView';
import CatalogPublishCard from './CatalogPublishCard';
import EdgesCard from './EdgesCard';
import StepEditorCard from './StepEditorCard';
import WorkflowIdempotencyCard, { type WorkflowIdempotencyConfig } from './WorkflowIdempotencyCard';
import IoEditorTab, { IoAiAssistCard } from './IoEditorTab';
import JsonAndSettingsCards from './JsonAndSettingsCards';
import { WORKFLOW_DAG_AI_SYSTEM_PROMPT, buildWorkflowDagAiUserPrompt } from '../../ai/workflowDagAiPrompt';

interface Props {
  selected?: WorkflowCatalogEntry | null;
  workers: Worker[];
  workersLoading: boolean;
  workersError: Error | null;
  /** Which handler catalog to use for step selection + AI context. */
  handlersSource?: 'v1' | 'v2';
}

type RightTab = 'Workflow' | 'Steps' | 'Edges' | 'JSON';

const HANDLERS_QUERY_KEY = ['admin', 'orchestrator', 'handlers'] as const;
const DEVICE_CONFIG_KEYS_QUERY_KEY = ['admin', 'orchestrator', 'devices', 'configKeys'] as const;

function tryParseJson(text: string): unknown {
  try {
    return safeParseJson(text);
  } catch {
    return {};
  }
}

function tryParseJsonObject(text: string): Record<string, any> {
  try {
    const parsed = safeParseJson(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, any>) : {};
  } catch {
    return {};
  }
}

function stableStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  const norm = (v: any): any => {
    if (v === null || v === undefined) return v;
    if (typeof v !== 'object') return v;
    if (seen.has(v)) return '[Circular]';
    seen.add(v);
    if (Array.isArray(v)) return v.map(norm);
    const out: Record<string, any> = {};
    Object.keys(v).sort((a, b) => a.localeCompare(b)).forEach((k) => {
      out[k] = norm(v[k]);
    });
    return out;
  };
  return JSON.stringify(norm(value));
}

function getSchemaPropertyKeys(schema: unknown): string[] {
  const obj = asRecord(schema);
  const props = asRecord(obj.properties);
  return Object.keys(props);
}

function wrapDagDefinition(dag: unknown): unknown {
  // Match orchestrator API contract: definition is wrapped as { "dag": { ... } }
  return { dag };
}

function normalizeHandlerConfig(config: unknown): unknown {
  if (config === undefined || config === null) return {};
  if (typeof config === 'string') {
    const trimmed = config.trim();
    if (!trimmed) return {};
    try {
      return safeParseJson(trimmed);
    } catch {
      return {};
    }
  }
  return config;
}

function asRecord(value: unknown): Record<string, any> {
  if (!value || typeof value !== 'object') return {};
  if (Array.isArray(value)) return {};
  return value as Record<string, any>;
}

function schemaTypeToString(schema: unknown): string | undefined {
  const obj = asRecord(schema);
  const t = obj.type;
  if (typeof t === 'string') return t;
  if (Array.isArray(t)) return t.filter((x) => typeof x === 'string').join('|') || undefined;
  if (obj.properties && typeof obj.properties === 'object' && !Array.isArray(obj.properties)) return 'object';
  if (obj.items != null) return 'array';
  return undefined;
}

function buildWorkflowMetadata(description: string, inputsSchema: unknown, outputsSchema: unknown): Record<string, unknown> | undefined {

  const desc = (description ?? '').trim();

  const extractIoDocs = (schema: unknown) => {
    const obj = asRecord(schema);
    const props = asRecord(obj.properties);
    const required = Array.isArray(obj.required)
      ? new Set(obj.required.filter((x) => typeof x === 'string'))
      : new Set<string>();

    const out: Array<Record<string, unknown>> = [];
    for (const name of Object.keys(props).sort()) {
      const prop = asRecord(props[name]);
      if (!Object.keys(prop).length) continue;

      const item: Record<string, unknown> = { name };
      const typeStr = schemaTypeToString(prop);
      if (typeStr) item.type = typeStr;

      const d = prop.description;
      if (typeof d === 'string' && d.trim()) item.description = d.trim();

      if (required.size > 0) item.required = required.has(name);

      out.push(item);
    }
    return out;
  };

  const inputs = extractIoDocs(inputsSchema);
  const outputs = extractIoDocs(outputsSchema);

  if (!desc && inputs.length === 0 && outputs.length === 0) return undefined;
  return {
    ...(desc ? { description: desc } : {}),
    ...(inputs.length ? { inputs } : {}),
    ...(outputs.length ? { outputs } : {}),
  };
}

function mapV2HandlerRowToLegacyHandler(row: HandlerSpecRow, workerIdByKey: Map<string, number>): Handler | null {
  const handler_key = String(row.handler_key ?? '').trim();
  if (!handler_key) return null;

  const worker_key = String(row.worker_key ?? '').trim();
  const worker_id = worker_key ? workerIdByKey.get(worker_key) : undefined;
  if (!worker_id) return null;

  const tags = Array.isArray(row.tags) ? row.tags : [];

  return {
    handler_key,
    worker_id,
    description: row.description != null ? String(row.description) : null,
    timeout_sec: Number.isFinite(Number(row.timeout_sec)) ? Number(row.timeout_sec) : 300,
    max_retries: Number.isFinite(Number(row.max_retries)) ? Number(row.max_retries) : 3,
    backoff_ms: Number.isFinite(Number(row.backoff_ms)) ? Number(row.backoff_ms) : 1000,
    priority: Number.isFinite(Number(row.priority)) ? Number(row.priority) : 0,
    category: null,
    tags,
    config: row.config,
    ability_key: null,
    is_active: row.is_active == null ? true : Boolean(row.is_active),
    created_at: new Date(0).toISOString(),
    updated_at: row.updated_at ? String(row.updated_at) : new Date(0).toISOString(),
  };
}

function pickString(value: unknown): string {
  return value == null ? '' : String(value);
}

function buildSyntheticWorkersAndIdMap(args: {
  workersV2Items?: Array<{ worker_key?: string; worker_kind?: string; transport?: string; is_active?: boolean }>;
  handlerRows?: HandlerSpecRow[];
}): { workers: Worker[]; workerIdByKey: Map<string, number> } {
  const infoByKey = new Map<string, { worker_kind?: string; transport?: string; is_active?: boolean }>();

  for (const w of args.workersV2Items ?? []) {
    const k = pickString(w?.worker_key).trim();
    if (!k) continue;
    infoByKey.set(k, {
      worker_kind: pickString((w as any)?.worker_kind).trim() || undefined,
      transport: pickString((w as any)?.transport).trim() || undefined,
      is_active: typeof (w as any)?.is_active === 'boolean' ? Boolean((w as any).is_active) : undefined,
    });
  }

  for (const row of args.handlerRows ?? []) {
    const k = pickString(row?.worker_key).trim();
    if (!k) continue;
    if (!infoByKey.has(k)) infoByKey.set(k, {});
  }

  const keys = Array.from(infoByKey.keys()).sort((a, b) => a.localeCompare(b));
  const workerIdByKey = new Map<string, number>();
  keys.forEach((k, idx) => workerIdByKey.set(k, idx + 1));

  const workers: Worker[] = keys.map((k) => {
    const info = infoByKey.get(k) ?? {};
    return {
      worker_id: workerIdByKey.get(k)!,
      worker_key: k,
      worker_kind: info.worker_kind ?? 'unknown',
      transport: info.transport ?? 'unknown',
      endpoint: undefined,
      endpoints: undefined,
      region: null,
      is_active: info.is_active == null ? true : Boolean(info.is_active),
      created_at: new Date(0).toISOString(),
    };
  });

  return { workers, workerIdByKey };
}

function collectInputRefs(value: unknown): string[] {
  const out = new Set<string>();
  const visit = (v: unknown) => {
    if (typeof v === 'string') {
      const tpl = /\{\{\s*([\s\S]*?)\s*\}\}/g;
      let t: RegExpExecArray | null;
      while ((t = tpl.exec(v)) !== null) {
        const inner = (t[1] ?? '').trim();
        if (!inner) continue;
        const re = /\binputs\.([a-zA-Z0-9_-]+)\b/g;
        let m: RegExpExecArray | null;
        while ((m = re.exec(inner)) !== null) {
          if (m[1]) out.add(m[1]);
        }
      }
      return;
    }
    if (Array.isArray(v)) {
      v.forEach(visit);
      return;
    }
    if (v && typeof v === 'object') {
      Object.values(v as Record<string, unknown>).forEach(visit);
    }
  };
  visit(value);
  return Array.from(out);
}

function collectStepOutputRefs(value: unknown): Array<{ stepId: string; outputKey: string }> {
  const out: Array<{ stepId: string; outputKey: string }> = [];
  const visit = (v: unknown) => {
    if (typeof v === 'string') {
      const tpl = /\{\{\s*([\s\S]*?)\s*\}\}/g;
      let t: RegExpExecArray | null;
      while ((t = tpl.exec(v)) !== null) {
        const inner = (t[1] ?? '').trim();
        if (!inner) continue;
        const re = /\bsteps\.([a-zA-Z0-9_-]+)\.outputs\.([a-zA-Z0-9_-]+)\b/g;
        let m: RegExpExecArray | null;
        while ((m = re.exec(inner)) !== null) {
          out.push({ stepId: m[1], outputKey: m[2] });
        }
      }
      return;
    }
    if (Array.isArray(v)) {
      v.forEach(visit);
      return;
    }
    if (v && typeof v === 'object') {
      Object.values(v as Record<string, unknown>).forEach(visit);
    }
  };
  visit(value);
  return out;
}

function isUpstream(edges: Edge[], from: string, to: string): boolean {
  if (from === to) return true;
  const adj = new Map<string, string[]>();
  edges.forEach((e) => {
    if (!adj.has(e.from)) adj.set(e.from, []);
    adj.get(e.from)!.push(e.to);
  });
  const q: string[] = [from];
  const seen = new Set<string>([from]);
  while (q.length > 0) {
    const cur = q.shift()!;
    for (const nxt of adj.get(cur) || []) {
      if (nxt === to) return true;
      if (seen.has(nxt)) continue;
      seen.add(nxt);
      q.push(nxt);
    }
  }
  return false;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function duplicateBaseId(stepId: string): string {
  // If the id ends with _<number>, treat it as a duplicate suffix.
  // Example: step_1_2 -> step_1
  return stepId.replace(/_\d+$/, '');
}

function nextDuplicateId(baseId: string, ids: string[]): string {
  const re = new RegExp(`^${escapeRegExp(baseId)}_(\\d+)$`);
  let max = 1;
  for (const id of ids) {
    const m = id.match(re);
    if (!m) continue;
    const n = Number.parseInt(m[1], 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return `${baseId}_${max + 1}`;
}

export default function WorkflowBuilderPanel({ selected, workers, workersLoading, workersError, handlersSource = 'v2' }: Props) {
  const qc = useQueryClient();
  const aiText = useAiText();
  const [error, setError] = React.useState<string | null>(null);
  const [saveToast, setSaveToast] = React.useState<null | { kind: 'success' | 'error'; message: string }>(null);
  const [savePulse, setSavePulse] = React.useState(false);
  const toastTimerRef = React.useRef<number | null>(null);
  const pulseTimerRef = React.useRef<number | null>(null);
  const [stepKeyDraft, setStepKeyDraft] = React.useState<string>('');
  const [rightTab, setRightTab] = React.useState<RightTab>('Workflow');
  const [wiringWarnings, setWiringWarnings] = React.useState<string[]>([]);
  const [diffActive, setDiffActive] = React.useState(false);

  const [dagAiOpen, setDagAiOpen] = React.useState(false);
  const [dagAiPrompt, setDagAiPrompt] = React.useState('');
  const [dagAiBusy, setDagAiBusy] = React.useState(false);
  const [dagAiError, setDagAiError] = React.useState<string | null>(null);

  const [existsConfirmOpen, setExistsConfirmOpen] = React.useState(false);
  const [existsConfirmBusy, setExistsConfirmBusy] = React.useState(false);
  const pendingPublishRef = React.useRef<null | { wk: string; ver: number; definition: unknown; metadata?: Record<string, unknown>; is_active: boolean }>(null);
  const pendingExistsMaxVersionRef = React.useRef<number>(0);

  React.useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
      if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
    };
  }, []);

  const baselineRef = React.useRef<{
    workflowKey: string;
    version: number;
    isActive: boolean;
    description: string;
    definition: unknown;
  } | null>(null);

  const {
    data: catalog,
    isLoading: catalogLoading,
    error: catalogError,
  } = useQuery<{ workers: Worker[]; handlers: Handler[] }, Error>({
    queryKey: [...HANDLERS_QUERY_KEY, 'workflow-builder', handlersSource],
    queryFn: async () => {
      if (handlersSource === 'v2') {
        const [workersRes, handlersRes] = await Promise.all([
          fetchWorkersV2Api({ limit: 500 }),
          fetchHandlersV2Api({ limit: 500, all_versions: true }),
        ]);

        const handlerRows: HandlerSpecRow[] = Array.isArray(handlersRes?.items) ? handlersRes.items : [];
        const { workers: syntheticWorkers, workerIdByKey } = buildSyntheticWorkersAndIdMap({
          workersV2Items: Array.isArray(workersRes?.items) ? (workersRes.items as any) : [],
          handlerRows,
        });

        const mapped: Handler[] = [];
        for (const row of handlerRows) {
          const h = mapV2HandlerRowToLegacyHandler(row, workerIdByKey);
          if (h) mapped.push(h);
        }

        return { workers: syntheticWorkers, handlers: mapped };
      }

      const legacy = await fetchHandlersApi({});
      return { workers, handlers: legacy };
    },
  });

  const effectiveWorkers = handlersSource === 'v2' ? (catalog?.workers ?? []) : workers;
  const effectiveWorkersLoading = handlersSource === 'v2' ? catalogLoading : workersLoading;
  const effectiveWorkersError = handlersSource === 'v2' ? (catalogError ?? null) : workersError;

  const handlers = catalog?.handlers ?? [];
  const handlersLoading = catalogLoading;
  const handlersError = catalogError;

  const {
    data: deviceConfigKeys = [],
    isLoading: deviceConfigKeysLoading,
    error: deviceConfigKeysError,
  } = useQuery<string[], Error>({
    queryKey: [...DEVICE_CONFIG_KEYS_QUERY_KEY, 'workflow-builder'],
    queryFn: fetchDeviceConfigKeysApi,
    staleTime: 30_000,
  });

  const handlerByKey = React.useMemo(() => {
    const map = new Map<string, Handler>();
    handlers.forEach((h) => map.set(h.handler_key, h));
    return map;
  }, [handlers]);

  // Catalog metadata
  const [workflowKey, setWorkflowKey] = React.useState('');
  // v2 publish supports server-side auto-bump (version=0); don't require manual selection.
  const [version, setVersion] = React.useState(handlersSource === 'v2' ? '0' : '1');
  const [isActive, setIsActive] = React.useState(true);
  const [workflowDescription, setWorkflowDescription] = React.useState('');

  const [workflowIdempotency, setWorkflowIdempotency] = React.useState<WorkflowIdempotencyConfig>({
    enabled: false,
    scope_namespace: '',
    scope_key_fields: [],
  });

  // DAG parts
  const [nodes, setNodes] = React.useState<StepNode[]>(() => ([
    {
      id: 'step_1',
      x: 0,
      y: 0,
      def: {
        type: 'cloud',
        service: '',
        device_capability: '',
        retries: { count: 0, backoff: 'linear' },
      },
      paramsDefaultsJson: '{}',
      paramsBindingsJson: '{}',
      overrideAllowlistJson: '[]',
    },
  ]));
  const [edges, setEdges] = React.useState<Edge[]>([]);
  const [selectedStepId, setSelectedStepId] = React.useState<string>('step_1');
  const [edgeFrom, setEdgeFrom] = React.useState('');
  const [edgeTo, setEdgeTo] = React.useState('');
  const [inputsSchemaJson, setInputsSchemaJson] = React.useState('{\n  "$schema": "https://json-schema.org/draft/2020-12/schema",\n  "type": "object",\n  "additionalProperties": true,\n  "properties": {},\n  "required": []\n}');

  const [outputsSchemaJson, setOutputsSchemaJson] = React.useState('{\n  "$schema": "https://json-schema.org/draft/2020-12/schema",\n  "type": "object",\n  "additionalProperties": true,\n  "properties": {},\n  "required": []\n}');
  const [outputsBindingsJson, setOutputsBindingsJson] = React.useState('{}');
  const [settings, setSettings] = React.useState<Settings>({
    max_concurrency: 1,
    timeout_seconds: 3600,
    default_retry: { count: 0, backoff: 'linear' },
  });

  // In v2 mode, the outcomes-projected workflow definition may have an empty/missing inputs_schema,
  // which makes the step "Run when inputs filled" checkbox list look wrong. Auto-infer inputs_schema
  // from current step bindings (without removing any existing keys).
  React.useEffect(() => {
    if (handlersSource !== 'v2') return;
    if (handlersLoading) return;

    const schema = tryParseJson(inputsSchemaJson);
    const existingKeys = new Set(getSchemaPropertyKeys(schema));

    const referencedKeys = new Set<string>();
    nodes.forEach((n) => {
      const bindings = tryParseJson(n.paramsBindingsJson);
      const bindingsObj = (bindings && typeof bindings === 'object' && !Array.isArray(bindings)) ? (bindings as Record<string, any>) : {};
      collectInputRefs(bindingsObj).forEach((k) => referencedKeys.add(k));
    });

    const missingAny = Array.from(referencedKeys).some((k) => !existingKeys.has(k));
    if (!missingAny) return;

    // Reuse the same logic as the manual Sync button.
    setWiringWarnings([]);
    const schemaObj = (schema && typeof schema === 'object' && !Array.isArray(schema)) ? (schema as Record<string, any>) : {};
    const next: Record<string, any> = { ...schemaObj };
    next.type = next.type || 'object';
    next.properties = (next.properties && typeof next.properties === 'object' && !Array.isArray(next.properties)) ? next.properties : {};
    next.required = Array.isArray(next.required) ? next.required : [];

    const requiredSet = new Set<string>(next.required.filter((x: any) => typeof x === 'string'));

    nodes.forEach((n) => {
      const bindings = tryParseJson(n.paramsBindingsJson);
      const bindingsObj = (bindings && typeof bindings === 'object' && !Array.isArray(bindings)) ? (bindings as Record<string, any>) : {};
      const handler = handlerByKey.get(n.def.service || '') ?? resolveHandlerRef(handlers, n.def.service || '');
      const cfg = handler?.config && typeof handler.config === 'object' && !Array.isArray(handler.config) ? (handler.config as any) : null;
      const paramsSchema = cfg?.params_schema;
      const requiredParams: string[] = paramsSchema?.mandatory && typeof paramsSchema.mandatory === 'object'
        ? Object.keys(paramsSchema.mandatory)
        : (Array.isArray(paramsSchema?.required) ? paramsSchema.required.filter((x: any) => typeof x === 'string') : []);

      Object.entries(bindingsObj).forEach(([param, val]) => {
        const refs = collectInputRefs(val);
        refs.forEach((key) => {
          if (!next.properties[key]) {
            next.properties[key] = { type: 'string' };
          }
          if (requiredParams.includes(param)) {
            requiredSet.add(key);
          }
        });
      });
    });

    next.required = Array.from(requiredSet).sort();
    setInputsSchemaJson(formatJson(next));
  }, [handlersSource, handlersLoading, handlers, handlerByKey, inputsSchemaJson, nodes]);

  const removeInputRefsFromValue = React.useCallback((value: unknown, inputKey: string): unknown => {
    if (typeof value === 'string') {
      // Remove template segments that reference inputs.<key>.
      // Keep it conservative: if the entire value is a single binding, clear it.
      const trimmed = value.trim();
      const exact = new RegExp(`^\\{\\{\\s*inputs\\.${inputKey.replace(/[-/\\\\^$*+?.()|[\\]{}]/g, '\\\\$&')}\\s*\\}\\}$`);
      if (exact.test(trimmed)) return '';

      // Otherwise, strip just the template parts that mention inputs.<key>.
      const tpl = /\{\{\s*([\s\S]*?)\s*\}\}/g;
      let changed = false;
      const replaced = value.replace(tpl, (full, innerRaw) => {
        const inner = String(innerRaw ?? '');
        const re = new RegExp(`\\binputs\\.${inputKey.replace(/[-/\\\\^$*+?.()|[\\]{}]/g, '\\\\$&')}\\b`);
        if (!re.test(inner)) return full;
        changed = true;
        return '';
      });
      return changed ? replaced.trim() : value;
    }
    if (Array.isArray(value)) {
      let any = false;
      const next = value.map((v) => {
        const out = removeInputRefsFromValue(v, inputKey);
        if (out !== v) any = true;
        return out;
      });
      return any ? next : value;
    }
    if (value && typeof value === 'object') {
      const obj = value as Record<string, any>;
      let any = false;
      const next: Record<string, any> = Array.isArray(obj) ? [] : { ...obj };
      for (const [k, v] of Object.entries(obj)) {
        const out = removeInputRefsFromValue(v, inputKey);
        if (out !== v) {
          any = true;
          next[k] = out;
        }
      }
      return any ? next : value;
    }
    return value;
  }, []);

  const handleRemoveWorkflowInputKey = React.useCallback((inputKey: string) => {
    // 1) Remove any step param bindings that reference the deleted input.
    setNodes((prev) => {
      let anyChanged = false;
      const next = prev.map((n) => {
        const parsed = safeParseJson(n.paramsBindingsJson);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return n;
        const cleaned = removeInputRefsFromValue(parsed, inputKey);
        if (cleaned === parsed) return n;
        anyChanged = true;
        return { ...n, paramsBindingsJson: formatJson(cleaned) };
      });
      return anyChanged ? next : prev;
    });

    // 2) Remove any outputs_bindings entries that reference the deleted input.
    setOutputsBindingsJson((prev) => {
      const parsed = safeParseJson(prev);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return prev;
      const obj = parsed as Record<string, any>;
      let any = false;
      const next: Record<string, any> = { ...obj };
      for (const [k, v] of Object.entries(obj)) {
        const cleaned = removeInputRefsFromValue(v, inputKey);
        if (cleaned !== v) {
          any = true;
          next[k] = cleaned;
        }
      }
      return any ? formatJson(next) : prev;
    });

    // 3) Remove from workflow-run idempotency scope.
    setWorkflowIdempotency((prev) => {
      const remaining = Array.isArray(prev.scope_key_fields)
        ? prev.scope_key_fields.filter((x) => x !== inputKey)
        : [];
      const enabled = remaining.length > 0 ? Boolean(prev.enabled) : false;
      return {
        ...prev,
        enabled,
        scope_namespace: enabled ? prev.scope_namespace : '',
        scope_key_fields: remaining,
      };
    });
  }, [removeInputRefsFromValue, setNodes, setOutputsBindingsJson, setWorkflowIdempotency]);

  React.useEffect(() => {
    if (!selected) return;

    const parsed = fromDag(selected.definition);

    // Persisted location: dag.settings.run_idempotency (stored inside the definition JSON).
    // Fallback: selected.metadata.idempotency (older drafts / non-table-backed lists).
    const settingsAny = (parsed.settings && typeof parsed.settings === 'object' && !Array.isArray(parsed.settings))
      ? (parsed.settings as any)
      : null;
    const rawFromSettings = settingsAny?.run_idempotency;
    const rawFromMetadata = (selected as any)?.metadata?.idempotency;
    const rawIdem = (rawFromSettings && typeof rawFromSettings === 'object' && !Array.isArray(rawFromSettings))
      ? rawFromSettings
      : ((rawFromMetadata && typeof rawFromMetadata === 'object' && !Array.isArray(rawFromMetadata)) ? rawFromMetadata : undefined);

    if (rawIdem) {
      const ns = typeof (rawIdem as any).scope_namespace === 'string' ? String((rawIdem as any).scope_namespace) : '';
      const fieldsRaw = Array.isArray((rawIdem as any).scope_key_fields) ? (rawIdem as any).scope_key_fields : [];
      const fields = fieldsRaw.map((x: any) => String(x ?? '').trim()).filter(Boolean);
      setWorkflowIdempotency({ enabled: true, scope_namespace: ns, scope_key_fields: fields });
    } else {
      setWorkflowIdempotency({ enabled: false, scope_namespace: '', scope_key_fields: [] });
    }

    setWorkflowKey(selected.workflow_key);
    setVersion(String(selected.version));
    setIsActive(Boolean(selected.is_active));

    baselineRef.current = {
      workflowKey: selected.workflow_key,
      version: selected.version,
      isActive: Boolean(selected.is_active),
      description: '',
      definition: selected.definition,
    };
    setDiffActive(false);

    if (parsed.nodes.length > 0) {
      setNodes(parsed.nodes);
      setEdges(parsed.edges);
      setSelectedStepId(parsed.nodes[0].id);
      setEdgeFrom(parsed.nodes[0].id);
      setEdgeTo(parsed.nodes[1]?.id ?? '');
      setInputsSchemaJson(formatJson(parsed.inputsSchema));
      setOutputsSchemaJson(formatJson(parsed.outputsSchema));
      setOutputsBindingsJson(formatJson(parsed.outputsBindings));
      setSettings(parsed.settings);

      const desc = (parsed.settings && typeof parsed.settings === 'object' && !Array.isArray(parsed.settings))
        ? (parsed.settings as any).description
        : undefined;
      setWorkflowDescription(typeof desc === 'string' ? desc : '');

      // Normalize baseline.definition so the editor doesn't show “dirty” due to ordering/normalization
      // differences between stored JSON and the builder's toDag() output.
      try {
        const settingsWithMeta = {
          ...parsed.settings,
          ...(typeof desc === 'string' && desc.trim() ? { description: desc.trim() } : {}),
        };
        const normalizedDag = toDag(
          selected.workflow_key,
          parsed.nodes,
          parsed.edges,
          parsed.inputsSchema,
          parsed.outputsSchema,
          parsed.outputsBindings,
          settingsWithMeta,
        );
        baselineRef.current = {
          ...baselineRef.current,
          description: typeof desc === 'string' ? desc : '',
          definition: wrapDagDefinition(normalizedDag),
        };
      } catch {
        baselineRef.current = {
          ...baselineRef.current,
          description: typeof desc === 'string' ? desc : '',
        };
      }
    }
  }, [selected]);

  const workflowKeyTrimmed = workflowKey.trim();
  const versionNumber = React.useMemo(() => {
    const v = Number.parseInt(version.trim(), 10);
    return Number.isFinite(v) ? v : NaN;
  }, [version]);

  const { data: workflowsForKey = [] } = useQuery<WorkflowCatalogEntry[], Error>({
    queryKey: ['admin', 'orchestrator', 'workflows', 'byKey', workflowKeyTrimmed, handlersSource],
    queryFn: () => fetchWorkflowsApi({ workflow_key: workflowKeyTrimmed }),
    // v2 workflow tab must not GET /workflow-catalog.
    enabled: handlersSource !== 'v2' && !!workflowKeyTrimmed,
    staleTime: 15_000,
  });

  const existingForVersion = React.useMemo(() => {
    if (!workflowKeyTrimmed) return null;
    if (!Number.isFinite(versionNumber) || versionNumber <= 0) return null;
    return workflowsForKey.find((w) => w.workflow_key === workflowKeyTrimmed && w.version === versionNumber) ?? null;
  }, [workflowKeyTrimmed, workflowsForKey, versionNumber]);

  const keyExists = workflowsForKey.length > 0;

  const nextVersionForKey = React.useMemo(() => {
    if (!keyExists) return 1;
    let max = 0;
    for (const w of workflowsForKey) {
      if (w.workflow_key !== workflowKeyTrimmed) continue;
      if (typeof w.version === 'number' && Number.isFinite(w.version) && w.version > max) max = w.version;
    }
    return max + 1;
  }, [keyExists, workflowKeyTrimmed, workflowsForKey]);

  const v2PublishMutation = useMutation<StartWorkflowResponseV2, Error, { wk: string; ver: number; definition: unknown; metadata?: Record<string, unknown>; is_active: boolean }>({
    mutationFn: async (payload) => publishWorkflowV2Api({
      workflow_key: payload.wk,
      version: payload.ver,
      definition: payload.definition,
      metadata: payload.metadata,
      is_active: payload.is_active,
    }),
    onSuccess: (_started, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'workflows', 'v2'] });
      window.setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'workflows', 'v2'] });
      }, 1500);

      setError(null);

      // v2 publish is async (starts a workflow run). Treat this as “submitted”.
      baselineRef.current = {
        workflowKey: vars.wk,
        version: vars.ver,
        isActive: Boolean(vars.is_active),
        description: (workflowDescription ?? '').trim(),
        definition: wrapDagDefinition((vars.definition as any)?.dag ?? (vars.definition as any)),
      };
      setDiffActive(false);

      setSaveToast({
        kind: 'success',
        message: 'Publish started (v2)',
      });
      setSavePulse(true);

      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = window.setTimeout(() => setSaveToast(null), 2400);

      if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = window.setTimeout(() => setSavePulse(false), 900);
    },
    onError: (err) => {
      const msg = err?.message || 'Failed to publish workflow (v2)';
      setError(msg);
      setSaveToast({ kind: 'error', message: msg });

      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = window.setTimeout(() => setSaveToast(null), 3200);
    },
  });

  const saveMutation = useMutation<WorkflowCatalogEntry, Error, { mode: 'create' | 'update'; wk: string; ver: number; definition: unknown; is_active: boolean }>({
    mutationFn: async (payload) => {
      if (payload.mode === 'update') {
        return updateWorkflowApi(payload.wk, payload.ver, { definition: payload.definition, is_active: payload.is_active });
      }
      return createWorkflowApi({
        workflow_key: payload.wk,
        version: payload.ver,
        definition: payload.definition,
        is_active: payload.is_active,
      });
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'workflows'] });
      qc.invalidateQueries({ queryKey: ['admin', 'orchestrator', 'workflows', 'byKey', workflowKeyTrimmed] });
      setError(null);

      // Normalize the editor state to whatever the server actually stored.
      // This prevents "dirty" staying true due to ordering/normalization differences.
      try {
        const parsed = fromDag(saved.definition);
        if (parsed.nodes.length > 0) {
          setNodes(parsed.nodes);
          setEdges(parsed.edges);
          setSelectedStepId(parsed.nodes[0].id);
          setEdgeFrom(parsed.nodes[0].id);
          setEdgeTo(parsed.nodes[1]?.id ?? '');
          setInputsSchemaJson(formatJson(parsed.inputsSchema));
          setOutputsSchemaJson(formatJson(parsed.outputsSchema));
          setOutputsBindingsJson(formatJson(parsed.outputsBindings));
          setSettings(parsed.settings);

          const desc = (parsed.settings && typeof parsed.settings === 'object' && !Array.isArray(parsed.settings))
            ? (parsed.settings as any).description
            : undefined;
          setWorkflowDescription(typeof desc === 'string' ? desc : '');

          // Also normalize baseline.definition using the builder's toDag() shape.
          const settingsWithMeta = {
            ...parsed.settings,
            ...(typeof desc === 'string' && desc.trim() ? { description: desc.trim() } : {}),
          };
          const normalizedDag = toDag(
            saved.workflow_key,
            parsed.nodes,
            parsed.edges,
            parsed.inputsSchema,
            parsed.outputsSchema,
            parsed.outputsBindings,
            settingsWithMeta,
          );
          baselineRef.current = {
            workflowKey: saved.workflow_key,
            version: saved.version,
            isActive: Boolean(saved.is_active),
            description: (typeof desc === 'string' ? desc : '').trim(),
            definition: wrapDagDefinition(normalizedDag),
          };
        }
      } catch {
        // If parsing fails for any reason, fall back to baseline reset only.
      }

      // If we didn't manage to normalize above, fall back to stored definition.
      if (!baselineRef.current || baselineRef.current.workflowKey !== saved.workflow_key || baselineRef.current.version !== saved.version) {
        baselineRef.current = {
          workflowKey: saved.workflow_key,
          version: saved.version,
          isActive: Boolean(saved.is_active),
          description: (workflowDescription ?? '').trim(),
          definition: saved.definition,
        };
      }
      setDiffActive(false);

      setWorkflowKey(saved.workflow_key);
      setVersion(String(saved.version));
      setIsActive(Boolean(saved.is_active));

      setSaveToast({
        kind: 'success',
        message: keyExists ? 'Workflow published (new version)' : 'Workflow published',
      });
      setSavePulse(true);

      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = window.setTimeout(() => setSaveToast(null), 2400);

      if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = window.setTimeout(() => setSavePulse(false), 900);
    },
    onError: (err) => {
      const msg = err?.message || 'Failed to save workflow';
      setError(msg);
      setSaveToast({ kind: 'error', message: msg });

      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = window.setTimeout(() => setSaveToast(null), 3200);
    },
  });

  const selectedNode = nodes.find((n) => n.id === selectedStepId) ?? nodes[0];
  const stepIds = nodes.map((n) => n.id);
  const layout = React.useMemo(() => computeTreeLayoutVertical(nodes, edges), [nodes, edges]);

  const selectedHandlerForJson = React.useMemo(() => {
    const key = selectedNode?.def.service?.trim();
    if (!key) return null;
    return handlerByKey.get(key) ?? resolveHandlerRef(handlers, key);
  }, [handlerByKey, handlers, selectedNode?.def.service]);

  const selectedHandlerConfigJson = React.useMemo(() => {
    if (!selectedHandlerForJson) return '';
    return formatJson(normalizeHandlerConfig(selectedHandlerForJson.config));
  }, [selectedHandlerForJson]);

  React.useEffect(() => {
    if (!selectedNode) return;
    setStepKeyDraft(selectedNode.id);
  }, [selectedNode?.id]);

  const definitionPreview = React.useMemo(() => {
    const wk = workflowKey.trim() || 'workflow_key';
    try {
      const idemNamespace = (workflowIdempotency.scope_namespace ?? '').trim() || (wk ? `workflow_run:${wk}` : '');
      const idemFields = Array.isArray(workflowIdempotency.scope_key_fields)
        ? workflowIdempotency.scope_key_fields.map((x) => String(x ?? '').trim()).filter(Boolean)
        : [];
      const idemEnabled = Boolean(workflowIdempotency.enabled) || idemFields.length > 0;

      const settingsWithMeta = {
        ...settings,
        ...(workflowDescription.trim() ? { description: workflowDescription.trim() } : {}),
        ...(idemEnabled && idemNamespace && idemFields.length > 0
          ? { run_idempotency: { scope_namespace: idemNamespace, scope_key_fields: idemFields } }
          : {}),
      };
      const dag = toDag(
        wk,
        nodes,
        edges,
        safeParseJson(inputsSchemaJson),
        safeParseJson(outputsSchemaJson),
        safeParseJson(outputsBindingsJson),
        settingsWithMeta,
      );
      // Match orchestrator API contract: definition is wrapped as { "dag": { ... } }
      return JSON.stringify({ dag }, null, 2);
    } catch {
      return '{\n  "error": "Invalid JSON in inputs schema or step params"\n}';
    }
  }, [workflowKey, nodes, edges, inputsSchemaJson, outputsSchemaJson, outputsBindingsJson, settings, workflowDescription, workflowIdempotency]);

  const hasDefinitionChanges = React.useMemo(() => {
    const baseline = baselineRef.current;
    if (!baseline) return false;
    return stableStringify(tryParseJson(definitionPreview)) !== stableStringify(baseline.definition);
  }, [definitionPreview]);

  const parseAiJson = (text: string): unknown => {
    const raw = text.trim();
    if (!raw) throw new Error('AI returned empty content.');
    try {
      return JSON.parse(raw);
    } catch {
      throw new Error('AI response was not valid JSON.');
    }
  };

  const applyDagAi = async () => {
    setDagAiError(null);

    const instruction = dagAiPrompt.trim();
    if (!instruction) {
      setDagAiError('Describe what you want to change (e.g. “add a step … wire it …”).');
      return;
    }

    const currentWorkflowKey = workflowKey.trim();
    if (!currentWorkflowKey) {
      setDagAiError('Workflow key is required before running Macro AI.');
      return;
    }

    const isUpdateMode = !!baselineRef.current;

    // Use the current in-editor DAG as context for incremental edits.
    let currentDag: unknown = null;
    try {
      const settingsWithMeta = {
        ...settings,
        ...(workflowDescription.trim() ? { description: workflowDescription.trim() } : {}),
      };
      currentDag = toDag(
        currentWorkflowKey,
        nodes,
        edges,
        safeParseJson(inputsSchemaJson),
        safeParseJson(outputsSchemaJson),
        safeParseJson(outputsBindingsJson),
        settingsWithMeta,
      );
    } catch {
      currentDag = null;
    }

    const specParts: string[] = [];
    specParts.push(`Mode: ${isUpdateMode ? 'UPDATE existing workflow (do not change workflow_key)' : 'CREATE/EDIT draft workflow (workflow_key may be set)'}`);
    specParts.push(`Current workflow_key: ${currentWorkflowKey}`);
    if (workflowDescription.trim()) specParts.push(`Current workflow_description: ${workflowDescription.trim()}`);
    specParts.push('User request:');
    specParts.push(instruction);
    if (currentDag) {
      specParts.push('');
      specParts.push('Existing DAG JSON (modify this DAG; keep unrelated steps as-is):');
      try {
        // Avoid huge prompts from pretty-printing.
        specParts.push(JSON.stringify(currentDag));
      } catch {
        // ignore
      }
    }
    const spec = specParts.join('\n');

    setDagAiBusy(true);
    try {
      const systemPrompt = WORKFLOW_DAG_AI_SYSTEM_PROMPT;
      const userPrompt = buildWorkflowDagAiUserPrompt(spec, { handlers_json: handlers });

      const res = await aiText.generate({
        system_prompt: systemPrompt,
        user_prompt: userPrompt,
        temperature: 0.2,
        max_output_tokens: 4096,
      });
      if (!res) return; // 202 async dispatch — result via SSE

      const json = parseAiJson(res.content);
      const obj = (json && typeof json === 'object' && !Array.isArray(json)) ? (json as Record<string, any>) : null;
      if (!obj) throw new Error('AI response must be a JSON object.');

      const nextWkRaw = typeof obj.workflow_key === 'string' ? obj.workflow_key.trim() : '';
      const nextDescRaw = typeof obj.workflow_description === 'string' ? obj.workflow_description.trim() : '';
      const dag = obj.dag;
      if (!dag || typeof dag !== 'object' || Array.isArray(dag)) throw new Error('AI response missing dag object.');

      const enforcedWorkflowKey = isUpdateMode ? currentWorkflowKey : (nextWkRaw || currentWorkflowKey);
      if (!enforcedWorkflowKey) throw new Error('workflow_key is required.');

      // Basic safety: ensure all chosen services exist in registry.
      const available = new Set(handlers.map((h) => h.handler_key));
      const availableBases = new Set(handlers
        .map((h) => String(h.handler_key ?? '').trim())
        .filter(Boolean)
        .map((hk) => hk.replace(/(?:\.v\d+|:v?\d+)$/i, '')));
      const steps = (dag as any).steps;
      if (!steps || typeof steps !== 'object' || Array.isArray(steps)) {
        throw new Error('AI dag.steps must be an object keyed by step id.');
      }
      for (const stepId of Object.keys(steps)) {
        const s = (steps as any)[stepId];
        const service = (s && typeof s === 'object') ? String((s as any).service ?? '') : '';
        if (!service.trim()) throw new Error(`AI step ${stepId} is missing service.`);
        const svc = service.trim();
        if (available.has(svc)) continue;
        const m = svc.match(/^(.*):latest$/i);
        if (m) {
          const base = String(m[1] ?? '').trim();
          if (base && availableBases.has(base)) continue;
        }
        throw new Error(`AI step ${stepId} uses unknown handler_key: ${service}`);
      }

      const parsed = fromDag({ dag });
      if (!parsed.nodes.length) throw new Error('AI returned a DAG with no steps.');

      setWorkflowKey(enforcedWorkflowKey);
      if (nextDescRaw) setWorkflowDescription(nextDescRaw);

      setNodes(parsed.nodes);
      setEdges(parsed.edges);
      setSelectedStepId(parsed.nodes[0].id);
      setEdgeFrom(parsed.nodes[0].id);
      setEdgeTo(parsed.nodes[1]?.id ?? '');
      setInputsSchemaJson(formatJson(parsed.inputsSchema));
      setOutputsSchemaJson(formatJson(parsed.outputsSchema));
      setOutputsBindingsJson(formatJson(parsed.outputsBindings));
      setSettings(parsed.settings);

      setDagAiOpen(false);
      setDagAiPrompt('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setDagAiError(msg || 'Macro AI failed.');
    } finally {
      setDagAiBusy(false);
    }
  };

  const hasUnsavedChanges = React.useMemo(() => {
    const baseline = baselineRef.current;
    if (!baseline) return false;
    if (hasDefinitionChanges) return true;
    if (workflowKeyTrimmed !== baseline.workflowKey) return true;
    if (Number.isFinite(versionNumber) && versionNumber !== baseline.version) return true;
    if (Boolean(isActive) !== baseline.isActive) return true;
    if ((workflowDescription ?? '').trim() !== (baseline.description ?? '').trim()) return true;
    return false;
  }, [hasDefinitionChanges, isActive, workflowDescription, workflowKeyTrimmed, versionNumber]);

  const saveButtonLabel = React.useMemo(() => {
    if (keyExists) return 'Publish new version';
    return 'Publish workflow';
  }, [keyExists]);

  const savePending = handlersSource === 'v2' ? v2PublishMutation.isPending : saveMutation.isPending;

  const showSaveAndDiffActions = React.useMemo(() => {
    // For a brand-new workflow (no baseline yet), always show Publish.
    if (!baselineRef.current) return true;
    // For existing workflows, only show actions if anything actually changed.
    return hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  const handleSaveWorkflow = () => {
    setError(null);
    const wk = workflowKeyTrimmed;
    if (!wk) {
      setError('Workflow key is required.');
      return;
    }
    let ver = versionNumber;
    // v2 publish is table-backed and supports server-side auto-bump.
    // To avoid accidental 409/idempotent "success" when the UI is sitting on an existing version,
    // always request auto-bump (version=0) for v2 publishes.
    if (handlersSource === 'v2') {
      ver = 0;
      if (version.trim() !== '0') setVersion('0');
    }
    if (!Number.isFinite(ver) || ver <= 0) {
      if (handlersSource === 'v2') {
        // version=0 => auto-bump in system.workflow.create (table-backed catalog)
        ver = 0;
        setVersion('0');
      } else {
        // If the key already exists, default to the next available version.
        if (keyExists) {
          ver = nextVersionForKey;
          setVersion(String(ver));
        } else {
          setError('Version must be a positive integer.');
          return;
        }
      }
    }
    if (handlersSource !== 'v2') {
      // Avoid overwriting an existing catalog entry. If the user is sitting on an existing version,
      // auto-bump to next version.
      if (keyExists && existingForVersion) {
        ver = nextVersionForKey;
        setVersion(String(ver));
      }
    }
    if (nodes.length === 0) {
      setError('Add at least one step.');
      return;
    }

    const wkForDefaults = wk;
    const idemNamespace = (workflowIdempotency.scope_namespace ?? '').trim() || (wkForDefaults ? `workflow_run:${wkForDefaults}` : '');
    const idemFields = Array.isArray(workflowIdempotency.scope_key_fields)
      ? workflowIdempotency.scope_key_fields.map((x) => String(x ?? '').trim()).filter(Boolean)
      : [];
    const idemRequested = Boolean(workflowIdempotency.enabled);
    const idemEnabled = idemRequested || idemFields.length > 0;

    if (idemRequested && idemFields.length === 0) {
      setError('Enable deterministic run scope requires selecting at least one input key field (use the “Scope” checkboxes in the Inputs table).');
      return;
    }

    let dag: unknown;
    let inputsSchema: unknown;
    let outputsSchema: unknown;
    try {
      const settingsWithMeta = {
        ...settings,
        ...(workflowDescription.trim() ? { description: workflowDescription.trim() } : {}),
        ...(idemEnabled && idemNamespace && idemFields.length > 0
          ? { run_idempotency: { scope_namespace: idemNamespace, scope_key_fields: idemFields } }
          : {}),
      };
      inputsSchema = safeParseJson(inputsSchemaJson);
      outputsSchema = safeParseJson(outputsSchemaJson);
      dag = toDag(
        wk,
        nodes,
        edges,
        inputsSchema,
        outputsSchema,
        safeParseJson(outputsBindingsJson),
        settingsWithMeta,
      );
    } catch {
      setError('Invalid JSON in inputs schema or step params.');
      return;
    }

    const md = buildWorkflowMetadata(workflowDescription, inputsSchema, outputsSchema) ?? {};
    const metadata = (
      Object.keys(md).length > 0 || (idemEnabled && idemNamespace && idemFields.length > 0)
    )
      ? {
        ...md,
        ...(idemEnabled && idemNamespace && idemFields.length > 0
          ? { idempotency: { scope_namespace: idemNamespace, scope_key_fields: idemFields } }
          : {}),
      }
      : undefined;

    // Preflight: if a workflow for this key already exists, ask before creating a new version.
    // (Discard means "do nothing".)
    const preflight = async () => {
      try {
        const rows = await fetchWorkflowsApi({ workflow_key: wk });
        const list = Array.isArray(rows) ? rows : [];
        let max = 0;
        for (const w of list) {
          if (w?.workflow_key !== wk) continue;
          if (typeof w.version === 'number' && Number.isFinite(w.version) && w.version > max) max = w.version;
        }

        // Decide the publish payload now, but gate execution on confirm if it already exists.
        const publishPayload = {
          wk,
          // v2: 0 => server-side auto-bump; v1: pick next version
          ver: (handlersSource === 'v2') ? 0 : (max > 0 ? max + 1 : 1),
          definition: { dag },
          metadata,
          is_active: isActive,
        };

        if (max > 0) {
          pendingPublishRef.current = publishPayload;
          pendingExistsMaxVersionRef.current = max;
          setExistsConfirmOpen(true);
          return;
        }

        if (handlersSource === 'v2') {
          v2PublishMutation.mutate(publishPayload);
        } else {
          saveMutation.mutate({ mode: 'create', wk: publishPayload.wk, ver: publishPayload.ver, definition: publishPayload.definition, is_active: publishPayload.is_active });
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg || 'Failed to check existing workflows.');
      }
    };

    void preflight();
  };

  const handleAddStep = () => {
    const existing = new Set(stepIds);
    const id = nextStepId(existing);
    setNodes((prev) => ([
      ...prev,
      {
        id,
        x: 0,
        y: 0,
        def: {
          type: 'cloud',
          service: '',
          device_capability: '',
          retries: { count: 0, backoff: 'linear' },
        },
        paramsDefaultsJson: '{}',
        paramsBindingsJson: '{}',
        overrideAllowlistJson: '[]',
      },
    ]));
    setSelectedStepId(id);
    setEdgeFrom((prev) => prev || id);
    if (!edgeTo) setEdgeTo(id);
  };

  const handleDuplicateSelectedStep = () => {
    const source = nodes.find((n) => n.id === selectedStepId);
    if (!source) return;

    const base = duplicateBaseId(source.id);
    const id = nextDuplicateId(base, stepIds);
    setNodes((prev) => ([
      ...prev,
      {
        ...source,
        id,
        x: 0,
        y: 0,
        def: { ...source.def, retries: { ...source.def.retries } },
      },
    ]));
    setSelectedStepId(id);
    setEdgeFrom((prev) => prev || id);
    setEdgeTo((prev) => prev || id);
  };

  const handleRemoveSelectedStep = () => {
    const id = selectedStepId;
    if (!id) return;
    if (nodes.length <= 1) {
      setError('Cannot remove the last step. A workflow must contain at least one step.');
      return;
    }

    const remaining = nodes.filter((n) => n.id !== id);
    const nextSelected = remaining[0]?.id ?? '';
    setNodes(remaining);
    setEdges((prev) => prev.filter((e) => e.from !== id && e.to !== id));
    setSelectedStepId(nextSelected);
    setEdgeFrom((prev) => (prev === id ? nextSelected : prev));
    setEdgeTo((prev) => (prev === id ? nextSelected : prev));
  };

  const handleAddEdge = () => {
    const from = edgeFrom;
    const to = edgeTo;
    if (!from || !to || from === to) return;
    if (!stepIds.includes(from) || !stepIds.includes(to)) return;
    const id = `e_${from}_to_${to}_${Date.now()}`;
    setEdges((prev) => [...prev, { id, from, to }]);
  };

  const ensureEdge = React.useCallback((fromRaw: string, toRaw: string) => {
    const from = (fromRaw ?? '').trim();
    const to = (toRaw ?? '').trim();
    if (!from || !to || from === to) return;
    if (!stepIds.includes(from) || !stepIds.includes(to)) return;
    setEdges((prev) => {
      if (prev.some((e) => e.from === from && e.to === to)) return prev;
      const id = `e_${from}_to_${to}_${Date.now()}`;
      return [...prev, { id, from, to }];
    });
  }, [stepIds]);

  const handleRemoveEdge = (edgeId: string) => {
    setEdges((prev) => prev.filter((e) => e.id !== edgeId));
  };

  const handleRenameStep = React.useCallback((oldId: string, nextIdRaw: string) => {
    const nextId = nextIdRaw.trim();
    if (!nextId) {
      setError('Step key is required.');
      return;
    }
    if (nextId === oldId) return;

    // Validate uniqueness against current nodes.
    if (nodes.some((n) => n.id === nextId)) {
      setError(`Step key already exists: ${nextId}`);
      return;
    }

    setError(null);
    setNodes((prev) => prev.map((n) => (n.id === oldId ? { ...n, id: nextId } : n)));
    setEdges((prev) => prev.map((e) => ({
      ...e,
      from: e.from === oldId ? nextId : e.from,
      to: e.to === oldId ? nextId : e.to,
    })));
    setSelectedStepId((prev) => (prev === oldId ? nextId : prev));
    setEdgeFrom((prev) => (prev === oldId ? nextId : prev));
    setEdgeTo((prev) => (prev === oldId ? nextId : prev));
  }, [nodes]);

  const updateSelected = (patch: Partial<StepDefinition> & { paramsDefaultsJson?: string; paramsBindingsJson?: string; overrideAllowlistJson?: string }) => {
    const id = selectedStepId;
    if (!id) return;
    setNodes((prev) => prev.map((n) => {
      if (n.id !== id) return n;
      const nextDef: StepDefinition = {
        ...n.def,
        ...patch,
        retries: patch.retries ? patch.retries : n.def.retries,
      };
      return {
        ...n,
        def: nextDef,
        paramsDefaultsJson: typeof patch.paramsDefaultsJson === 'string' ? patch.paramsDefaultsJson : n.paramsDefaultsJson,
        paramsBindingsJson: typeof patch.paramsBindingsJson === 'string' ? patch.paramsBindingsJson : n.paramsBindingsJson,
        overrideAllowlistJson: typeof patch.overrideAllowlistJson === 'string' ? patch.overrideAllowlistJson : n.overrideAllowlistJson,
      };
    }));
  };

  const handleSyncInputsSchemaFromBindings = () => {
    setWiringWarnings([]);
    const current = tryParseJson(inputsSchemaJson);
    const schema = (current && typeof current === 'object' && !Array.isArray(current)) ? (current as Record<string, any>) : {};
    const next: Record<string, any> = { ...schema };
    next.type = next.type || 'object';
    next.properties = (next.properties && typeof next.properties === 'object' && !Array.isArray(next.properties)) ? next.properties : {};
    next.required = Array.isArray(next.required) ? next.required : [];

    const requiredSet = new Set<string>(next.required.filter((x: any) => typeof x === 'string'));

    // Infer required runtime inputs only when bound from required handler params.
    nodes.forEach((n) => {
      const bindings = tryParseJson(n.paramsBindingsJson);
      const bindingsObj = (bindings && typeof bindings === 'object' && !Array.isArray(bindings)) ? (bindings as Record<string, any>) : {};
      const handler = handlerByKey.get(n.def.service || '') ?? resolveHandlerRef(handlers, n.def.service || '');
      const cfg = handler?.config && typeof handler.config === 'object' && !Array.isArray(handler.config) ? (handler.config as any) : null;
      const paramsSchema = cfg?.params_schema;
      const requiredParams: string[] = paramsSchema?.mandatory && typeof paramsSchema.mandatory === 'object'
        ? Object.keys(paramsSchema.mandatory)
        : (Array.isArray(paramsSchema?.required) ? paramsSchema.required.filter((x: any) => typeof x === 'string') : []);

      Object.entries(bindingsObj).forEach(([param, val]) => {
        const refs = collectInputRefs(val);
        refs.forEach((key) => {
          if (!next.properties[key]) {
            next.properties[key] = { type: 'string' };
          }
          if (requiredParams.includes(param)) {
            requiredSet.add(key);
          }
        });
      });
    });

    next.required = Array.from(requiredSet).sort();
    setInputsSchemaJson(formatJson(next));
  };

  const handleSyncOutputsSchemaFromBindings = () => {
    setWiringWarnings([]);
    const current = tryParseJson(outputsSchemaJson);
    const schema = (current && typeof current === 'object' && !Array.isArray(current)) ? (current as Record<string, any>) : {};
    const next: Record<string, any> = { ...schema };
    next.type = next.type || 'object';
    next.properties = (next.properties && typeof next.properties === 'object' && !Array.isArray(next.properties)) ? next.properties : {};

    const bindings = tryParseJson(outputsBindingsJson);
    const bindingsObj = (bindings && typeof bindings === 'object' && !Array.isArray(bindings)) ? (bindings as Record<string, any>) : {};

    const requiredSet = new Set<string>(Object.keys(bindingsObj));
    Object.keys(bindingsObj).forEach((k) => {
      if (!next.properties[k]) {
        next.properties[k] = { type: 'string' };
      }
    });
    next.required = Array.from(requiredSet).sort();
    setOutputsSchemaJson(formatJson(next));
  };

  const handleValidateWiring = () => {
    const warnings: string[] = [];
    const schema = tryParseJson(inputsSchemaJson);
    const schemaObj = (schema && typeof schema === 'object' && !Array.isArray(schema)) ? (schema as Record<string, any>) : {};
    const props = (schemaObj.properties && typeof schemaObj.properties === 'object' && !Array.isArray(schemaObj.properties))
      ? (schemaObj.properties as Record<string, any>)
      : {};
    const schemaKeys = new Set(Object.keys(props));
    const nodeById = new Map(nodes.map((n) => [n.id, n] as const));

    nodes.forEach((n) => {
      const bindings = tryParseJson(n.paramsBindingsJson);
      const bindingsObj = (bindings && typeof bindings === 'object' && !Array.isArray(bindings)) ? (bindings as Record<string, any>) : {};
      const inputRefs = collectInputRefs(bindingsObj);
      inputRefs.forEach((key) => {
        if (!schemaKeys.has(key)) {
          warnings.push(`Missing inputs_schema.properties.${key} (referenced by steps.${n.id}.params_bindings).`);
        }
      });

      const stepRefs = collectStepOutputRefs(bindingsObj);
      stepRefs.forEach(({ stepId, outputKey }) => {
        const refNode = nodeById.get(stepId);
        if (!refNode) {
          warnings.push(`Binding references missing step: {{steps.${stepId}.outputs.${outputKey}}} used by steps.${n.id}.`);
          return;
        }
        if (!isUpstream(edges, stepId, n.id)) {
          warnings.push(`Binding references non-upstream step output: steps.${stepId} is not upstream of steps.${n.id} (missing edge/path).`);
        }

        const handler = handlerByKey.get(refNode.def.service || '') ?? resolveHandlerRef(handlers, refNode.def.service || '');
        const cfg = handler?.config && typeof handler.config === 'object' && !Array.isArray(handler.config) ? (handler.config as any) : null;
        const outputsSchema = cfg?.outputs_schema;
        const outputsList: string[] = Array.isArray(cfg?.outputs) ? cfg.outputs.filter((x: any) => typeof x === 'string') : [];
        const outputKeys = outputsSchema?.properties && typeof outputsSchema.properties === 'object'
          ? Object.keys(outputsSchema.properties)
          : outputsList;
        if (outputKeys.length === 0) {
          warnings.push(`Cannot validate outputs for step ${stepId}: handler config missing outputs_schema/outputs list.`);
          return;
        }
        if (!outputKeys.includes(outputKey)) {
          warnings.push(`Unknown output key '${outputKey}' for step ${stepId}. Known: ${outputKeys.join(', ')}`);
        }
      });
    });

    // Validate workflow outputs bindings against steps + (if available) handler outputs.
    const outBindings = tryParseJsonObject(outputsBindingsJson);
    Object.entries(outBindings).forEach(([outKey, val]) => {
      if (typeof val !== 'string') {
        warnings.push(`Workflow outputs_bindings.${outKey} must be a string binding.`);
        return;
      }
      const refs = collectStepOutputRefs(val);
      if (refs.length === 0) {
        warnings.push(`Workflow outputs_bindings.${outKey} must reference a step output like {{steps.<id>.outputs.<key>}}.`);
        return;
      }
      refs.forEach(({ stepId, outputKey }) => {
        const refNode = nodeById.get(stepId);
        if (!refNode) {
          warnings.push(`Workflow outputs_bindings.${outKey} references missing step: ${stepId}.`);
          return;
        }
        const handler = handlerByKey.get(refNode.def.service || '') ?? resolveHandlerRef(handlers, refNode.def.service || '');
        const cfg = handler?.config && typeof handler.config === 'object' && !Array.isArray(handler.config) ? (handler.config as any) : null;
        const outputsSchema2 = cfg?.outputs_schema;
        const outputsList: string[] = Array.isArray(cfg?.outputs) ? cfg.outputs.filter((x: any) => typeof x === 'string') : [];
        const known = outputsSchema2?.properties && typeof outputsSchema2.properties === 'object'
          ? Object.keys(outputsSchema2.properties)
          : outputsList;
        if (known.length > 0 && !known.includes(outputKey)) {
          warnings.push(`Workflow outputs_bindings.${outKey} uses unknown output '${outputKey}' for step ${stepId}. Known: ${known.join(', ')}`);
        }
      });
    });

    setWiringWarnings(Array.from(new Set(warnings)));
  };

  const inputKeysForGraph = React.useMemo(() => {
    const obj = tryParseJsonObject(inputsSchemaJson);
    const props = obj.properties && typeof obj.properties === 'object' && !Array.isArray(obj.properties) ? obj.properties : {};
    return Object.keys(props).sort((a, b) => a.localeCompare(b));
  }, [inputsSchemaJson]);

  const inputMetaByKeyForGraph = React.useMemo(() => {
    const obj = tryParseJsonObject(inputsSchemaJson);
    const props = obj.properties && typeof obj.properties === 'object' && !Array.isArray(obj.properties)
      ? (obj.properties as Record<string, any>)
      : {};

    const out: Record<string, { type?: string; description?: string }> = {};
    Object.entries(props).forEach(([k, v]) => {
      const def = v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, any>) : {};
      const typeVal = def.type;
      const type = typeof typeVal === 'string'
        ? typeVal
        : (Array.isArray(typeVal) ? typeVal.filter((x: any) => typeof x === 'string').join('|') : undefined);
      const description = typeof def.description === 'string' ? def.description : undefined;
      out[k] = { type, description };
    });
    return out;
  }, [inputsSchemaJson]);

  const outputMetaByKeyForGraph = React.useMemo(() => {
    const obj = tryParseJsonObject(outputsSchemaJson);
    const props = obj.properties && typeof obj.properties === 'object' && !Array.isArray(obj.properties)
      ? (obj.properties as Record<string, any>)
      : {};

    const out: Record<string, { type?: string; description?: string }> = {};
    Object.entries(props).forEach(([k, v]) => {
      const def = v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, any>) : {};
      const typeVal = def.type;
      const type = typeof typeVal === 'string'
        ? typeVal
        : (Array.isArray(typeVal) ? typeVal.filter((x: any) => typeof x === 'string').join('|') : undefined);
      const description = typeof def.description === 'string' ? def.description : undefined;
      out[k] = { type, description };
    });
    return out;
  }, [outputsSchemaJson]);

  const outputKeysForGraph = React.useMemo(() => {
    const obj = tryParseJsonObject(outputsBindingsJson);
    return Object.keys(obj);
  }, [outputsBindingsJson]);

  return (
    <div className="h-full min-h-0 flex flex-col gap-4">
      <div className="pointer-events-none fixed right-4 top-4 z-50" aria-live="polite" aria-atomic="true">
        {saveToast && (
          <div
            className={
              'pointer-events-auto rounded-md border px-3 py-2 text-sm shadow-sm backdrop-blur ' +
              (saveToast.kind === 'success'
                ? 'pac-glass border-emerald-500/40 text-neutral-100'
                : 'pac-glass border-rose-500/40 text-neutral-100')
            }
          >
            <div className="flex items-start gap-2">
              <div
                className={
                  'mt-1 h-2 w-2 rounded-full ' +
                  (saveToast.kind === 'success' ? 'bg-emerald-400' : 'bg-rose-400')
                }
              />
              <div className="leading-5">{saveToast.message}</div>
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-rose-400">{error}</p>}

      {(layout.hasCycle || layout.invalidEdges > 0) && (
        <div className="text-xs text-amber-300 bg-amber-400/10 border border-amber-400/30 rounded px-3 py-2">
          {layout.hasCycle && <div>Cycle detected: graph cannot be topologically laid out. Showing fallback ordering.</div>}
          {layout.invalidEdges > 0 && <div>Some edges reference missing steps and are ignored in layout.</div>}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 flex-1 min-h-0">
        <WorkflowGraphView
          nodes={nodes}
          edges={edges}
          selectedStepId={selectedStepId}
          selectedStepLabel={stepKeyDraft}
          layout={layout}
          orientation="vertical"
          inputKeys={inputKeysForGraph}
          inputMetaByKey={inputMetaByKeyForGraph}
          outputMetaByKey={outputMetaByKeyForGraph}
          outputKeys={outputKeysForGraph}
          outputsBindingsJson={outputsBindingsJson}
          onSelect={setSelectedStepId}
        />

        <div className="h-full min-h-0 flex flex-col gap-3">
          <div className="max-w-full overflow-x-auto shrink-0 flex items-center justify-between gap-3">
            <Tabs
              tabs={['Workflow', 'Steps', 'Edges', 'JSON']}
              activeTab={rightTab}
              onTabChange={(t) => setRightTab(t as RightTab)}
            />

            <div className="flex items-center gap-2">
              {showSaveAndDiffActions && (
                <>
                  {!!baselineRef.current && (
                    <button
                      className="px-3 py-1 rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500"
                      onClick={() => setDiffActive((v) => !v)}
                      title="Toggle diff highlighting."
                    >
                      Diff
                    </button>
                  )}

                  <button
                    className={
                      `px-3 py-1 rounded border disabled:opacity-60 transition ` +
                      (hasUnsavedChanges
                        ? 'border-indigo-400 text-indigo-200 hover:bg-indigo-400/10'
                        : 'border-emerald-400 text-emerald-200 hover:bg-emerald-400/10')
                      + (savePulse ? ' ring-2 ring-emerald-500/40' : '')
                    }
                    onClick={handleSaveWorkflow}
                    disabled={savePending}
                    title="Publish workflow (creates a new version if this key already exists)"
                  >
                    {savePending ? (handlersSource === 'v2' ? 'Publishing' : 'Saving') : saveButtonLabel}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-auto pr-1">
            {rightTab === 'Workflow' && (
              <div className="space-y-3">
                <IoAiAssistCard
                  workflowKey={workflowKey}
                  workflowDescription={workflowDescription}
                  nodes={nodes}
                  isUpdateMode={!!baselineRef.current}
                  onChangeWorkflowKey={setWorkflowKey}
                  onChangeWorkflowDescription={setWorkflowDescription}
                  onChangeInputsSchemaJson={setInputsSchemaJson}
                  onChangeOutputsSchemaJson={setOutputsSchemaJson}
                  onChangeOutputsBindingsJson={setOutputsBindingsJson}
                />

                <CatalogPublishCard
                  workflowKey={workflowKey}
                  description={workflowDescription}
                  isActive={isActive}
                  baseline={baselineRef.current}
                  diffActive={diffActive}
                  hasDefinitionChanges={hasDefinitionChanges}
                  onChangeWorkflowKey={setWorkflowKey}
                  onChangeDescription={setWorkflowDescription}
                  onChangeIsActive={setIsActive}
                />

                {handlersSource === 'v2' && (
                  <WorkflowIdempotencyCard
                    workflowKey={workflowKey}
                    value={workflowIdempotency}
                    onChange={setWorkflowIdempotency}
                  />
                )}

                <IoEditorTab
                  workflowKey={workflowKey}
                  workflowDescription={workflowDescription}
                  inputsSchemaJson={inputsSchemaJson}
                  outputsSchemaJson={outputsSchemaJson}
                  outputsBindingsJson={outputsBindingsJson}
                  nodes={nodes}
                  handlers={handlers}
                  idempotency={handlersSource === 'v2'
                    ? {
                      enabled: Boolean(workflowIdempotency.enabled),
                      scopeKeyFields: workflowIdempotency.scope_key_fields,
                      onChangeScopeKeyFields: (next) => setWorkflowIdempotency((prev) => {
                        const wk = workflowKey.trim();
                        const fields = Array.isArray(next)
                          ? next.map((x) => String(x ?? '').trim()).filter(Boolean)
                          : [];
                        const enabled = fields.length > 0;
                        const prevNs = (prev.scope_namespace ?? '').trim();
                        const scope_namespace = enabled
                          ? (prevNs || (wk ? `workflow_run:${wk}` : ''))
                          : '';
                        return {
                          ...prev,
                          enabled,
                          scope_namespace,
                          scope_key_fields: fields,
                        };
                      }),
                    }
                    : undefined}
                  onChangeWorkflowKey={setWorkflowKey}
                  onChangeWorkflowDescription={setWorkflowDescription}
                  onChangeInputsSchemaJson={setInputsSchemaJson}
                  onChangeOutputsSchemaJson={setOutputsSchemaJson}
                  onChangeOutputsBindingsJson={setOutputsBindingsJson}
				  onRemoveInputKey={handleRemoveWorkflowInputKey}
                />
              </div>
            )}

            {rightTab === 'Edges' && (
              <EdgesCard
                stepIds={stepIds}
                edges={edges}
                edgeFrom={edgeFrom}
                edgeTo={edgeTo}
                onChangeEdgeFrom={setEdgeFrom}
                onChangeEdgeTo={setEdgeTo}
                onAddEdge={handleAddEdge}
                onRemoveEdge={handleRemoveEdge}
              />
            )}

            {rightTab === 'Steps' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500"
                    onClick={handleAddStep}
                  >
                    Add step
                  </button>

                  <button
                    className="px-3 py-1 rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500 disabled:opacity-60"
                    onClick={() => {
                      setDagAiError(null);
                      setDagAiOpen(true);
                    }}
                    disabled={handlersLoading || dagAiBusy}
                    title={handlersLoading ? 'Loading handler catalog…' : 'Use AI to add/modify steps and wiring (uses handler catalog).'}
                  >
                    Macro AI
                  </button>

                  <button
                    className="px-3 py-1 rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500"
                    onClick={handleDuplicateSelectedStep}
                    disabled={!selectedStepId}
                  >
                    Duplicate step
                  </button>
                  <button
                    className="px-3 py-1 rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500"
                    onClick={handleRemoveSelectedStep}
                    disabled={!selectedStepId}
                  >
                    Remove step
                  </button>
                </div>

                <StepEditorCard
                  selectedNode={selectedNode}
                  nodes={nodes}
                  edges={edges}
                  workers={effectiveWorkers}
                  workersLoading={effectiveWorkersLoading}
                  workersError={effectiveWorkersError}
                  handlers={handlers}
                  handlersLoading={handlersLoading}
                  handlersError={handlersError ?? null}
                  deviceConfigKeys={deviceConfigKeys}
                  deviceConfigKeysLoading={deviceConfigKeysLoading}
                  deviceConfigKeysError={deviceConfigKeysError ?? null}
                  inputsSchemaJson={inputsSchemaJson}
                  onUpdate={updateSelected}
                  onEnsureEdge={ensureEdge}
                  onRenameStep={handleRenameStep}
                  stepKeyDraft={stepKeyDraft}
                  onChangeStepKeyDraft={setStepKeyDraft}
                />

                <AppDialog
                  open={dagAiOpen}
                  onOpenChange={(open) => {
                    setDagAiOpen(open);
                    if (!open) setDagAiError(null);
                  }}
                  title="Macro AI (Steps + Wiring)"
                  description={!!baselineRef.current
                    ? 'Update mode: AI will modify steps/edges/IO but will NOT change the workflow key.'
                    : 'Create mode: AI may set workflow key and generate the full DAG.'}
                  size="lg"
                  onConfirm={applyDagAi}
                  confirmLabel={dagAiBusy ? 'Running…' : 'Apply'}
                  confirmDisabled={dagAiBusy || handlersLoading}
                  isBusy={dagAiBusy}
                >
                  <div className="space-y-3">
                    {(dagAiError || handlersError) && (
                      <p className="text-sm text-rose-400">
                        {dagAiError || (handlersError ? handlersError.message : '')}
                      </p>
                    )}

                    <div>
                      <div className="text-xs text-neutral-400 mb-1">
                        Tell AI what to do (example: “Add a download step after create_directory, wire its destination_dir to {'{{steps.create_directory.outputs.path}}'}, and bind workflow output saved_file_path to the download step output.”)
                      </div>
                      <textarea
                        className="w-full min-h-[140px] rounded pac-glass text-neutral-200 text-sm p-2 outline-none focus:border-neutral-500"
                        value={dagAiPrompt}
                        onChange={(e) => setDagAiPrompt(e.target.value)}
                        placeholder="Add a step … wire it …"
                      />
                    </div>

                    <div className="text-xs text-neutral-500">
                      Uses handler catalog ({handlers.length} handlers loaded) and current DAG as context.
                    </div>
                  </div>
                </AppDialog>
              </div>
            )}

            {rightTab === 'JSON' && (
              <JsonAndSettingsCards
                selectedHandlerKey={selectedHandlerForJson?.handler_key}
                selectedHandlerConfigJson={selectedHandlerConfigJson}
                inputsSchemaJson={inputsSchemaJson}
                outputsSchemaJson={outputsSchemaJson}
                outputsBindingsJson={outputsBindingsJson}
                settings={settings}
                definitionPreview={definitionPreview}
                onChangeInputsSchemaJson={setInputsSchemaJson}
                onChangeOutputsSchemaJson={setOutputsSchemaJson}
                onChangeOutputsBindingsJson={setOutputsBindingsJson}
                onSyncInputsSchemaFromBindings={handleSyncInputsSchemaFromBindings}
                onSyncOutputsSchemaFromBindings={handleSyncOutputsSchemaFromBindings}
                onValidateWiring={handleValidateWiring}
                wiringWarnings={wiringWarnings}
                onChangeSettings={setSettings}
              />
            )}
          </div>
        </div>
      </div>

      <AppDialog
        open={existsConfirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            setExistsConfirmBusy(false);
            pendingPublishRef.current = null;
            pendingExistsMaxVersionRef.current = 0;
          }
          setExistsConfirmOpen(open);
        }}
        title="Workflow already exists"
        description={`A workflow with this key already exists (latest version: ${pendingExistsMaxVersionRef.current}). Create a new version?`}
        size="md"
        footer={
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              className="px-3 py-1 rounded bg-neutral-700 hover:bg-neutral-600 disabled:opacity-70"
              onClick={() => setExistsConfirmOpen(false)}
              disabled={existsConfirmBusy}
            >
              Discard
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-neutral-700 disabled:cursor-not-allowed"
              disabled={existsConfirmBusy}
              onClick={async () => {
                const pending = pendingPublishRef.current;
                if (!pending) {
                  setExistsConfirmOpen(false);
                  return;
                }
                setExistsConfirmBusy(true);
                try {
                  if (handlersSource === 'v2') {
                    v2PublishMutation.mutate(pending);
                  } else {
                    saveMutation.mutate({ mode: 'create', wk: pending.wk, ver: pending.ver, definition: pending.definition, is_active: pending.is_active });
                  }
                  setExistsConfirmOpen(false);
                } finally {
                  setExistsConfirmBusy(false);
                }
              }}
            >
              Create new version
            </button>
          </div>
        }
      >
        <div className="text-sm text-neutral-300">
          Publishing will create a new immutable catalog entry for the same workflow key.
        </div>
      </AppDialog>
    </div>
  );
}
